import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import useStore from "../store/useStore";
import api from "../services/api";
import { resolveAssetUrl } from "../utils/assetUrl";

const initialForm = {
  platformName: "GAMRENT",
  tagline: "Property Management Platform",
  email: "",
  phone: "",
  whatsapp: "",
  address: "",
  logo: "",
  websiteUrl: "",
  defaultCurrency: "GMD",
  contactEmail: "",
  paymentInstructions: "",
  invoiceFooter: "",
  signupEnabled: true
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const SuperAdminSystemSettings = () => {
  const { toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const logoPreviewUrl = resolveAssetUrl(form.logo);
  const platformInitial = (form.platformName || "G").charAt(0).toUpperCase();

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/system-settings");
      setForm({ ...initialForm, ...(res.data?.data || {}) });
    } catch (error) {
      console.error("Load system settings error:", error);
      toast(error.response?.data?.message || "Failed to load system settings", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleLogoFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast("Please choose an image file for the platform logo", "error");
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast("Logo image must be 2MB or smaller", "error");
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(selectedFile);
      setForm((prev) => ({ ...prev, logo: dataUrl }));
      toast("Logo image selected", "success");
    } catch (error) {
      toast("Failed to read logo image", "error");
    }
  };

  const handleSave = async () => {
    if (!form.platformName.trim() || !form.email.trim()) {
      toast("Platform name and support email are required", "error");
      return;
    }

    try {
      setSaving(true);
      const res = await api.put("/system-settings", {
        platformName: form.platformName.trim(),
        tagline: form.tagline.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        whatsapp: form.whatsapp.trim(),
        address: form.address.trim(),
        logo: form.logo.trim(),
        websiteUrl: form.websiteUrl.trim(),
        defaultCurrency: form.defaultCurrency,
        contactEmail: form.contactEmail.trim() || form.email.trim(),
        paymentInstructions: form.paymentInstructions.trim(),
        invoiceFooter: form.invoiceFooter.trim(),
        signupEnabled: Boolean(form.signupEnabled)
      });
      setForm({ ...initialForm, ...(res.data?.data || {}) });
      toast(res.data?.message || "System settings updated successfully", "success");
    } catch (error) {
      toast(error.response?.data?.message || "Failed to update system settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="System Settings" />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-gray-600">Loading system settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-teal-100 bg-gradient-to-br from-teal-900 via-slate-900 to-slate-800 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="grid gap-6 px-6 py-8 md:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.9fr)] md:px-8 md:py-10">
          <div className="space-y-4">
            <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-teal-100">
              Platform control
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl">System Settings</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-teal-50/90 md:text-base">
                These details appear on the public website, subscription invoices, payment receipts, and contact messages.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Platform logo preview" className="h-16 w-16 rounded-2xl bg-white object-contain p-2 ring-1 ring-white/15" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-2xl font-black text-white ring-1 ring-white/15">
                  {platformInitial}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-100/80">Public identity</p>
                <p className="text-lg font-bold text-white">{form.platformName || "GAMRENT"}</p>
                <p className="text-sm text-teal-50/80">{form.email || "Support email"}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Phone</p>
                <p className="mt-1 font-semibold text-white">{form.phone || "N/A"}</p>
              </div>
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Signups</p>
                <p className="mt-1 font-semibold text-white">{form.signupEnabled ? "Open" : "Closed"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <h2 className="text-xl font-bold text-gray-900">Platform identity</h2>
        <p className="text-sm text-gray-600 mb-6">Name, tagline, and logo used across Gamrent.</p>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Platform Name <span className="text-red-500">*</span></label>
            <input name="platformName" value={form.platformName} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tagline</label>
            <input name="tagline" value={form.tagline} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Website URL</label>
            <input name="websiteUrl" value={form.websiteUrl} onChange={onChange} placeholder="https://gamrent.gm" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Default Currency</label>
            <select name="defaultCurrency" value={form.defaultCurrency} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="GMD">GMD</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Logo Image</label>
            <input type="file" accept="image/*" onChange={handleLogoFileChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white" />
            <p className="text-xs text-gray-500 mt-2">PNG, JPG, or SVG up to 2MB.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Logo URL</label>
            <input name="logo" value={form.logo} onChange={onChange} placeholder="https://example.com/logo.png" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <h2 className="text-xl font-bold text-gray-900">Public contact</h2>
        <p className="text-sm text-gray-600 mb-6">Shown on the website contact page and footer.</p>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Support Email <span className="text-red-500">*</span></label>
            <input type="email" name="email" value={form.email} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Form Inbox</label>
            <input type="email" name="contactEmail" value={form.contactEmail} onChange={onChange} placeholder="Where website messages are delivered" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
            <input name="phone" value={form.phone} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp</label>
            <input name="whatsapp" value={form.whatsapp} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
            <input name="address" value={form.address} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <h2 className="text-xl font-bold text-gray-900">Billing and access</h2>
        <p className="text-sm text-gray-600 mb-6">Used on subscription invoices, receipts, and new company signups.</p>
        <div className="grid gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Instructions</label>
            <textarea name="paymentInstructions" rows="3" value={form.paymentInstructions} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice / Receipt Footer</label>
            <textarea name="invoiceFooter" rows="2" value={form.invoiceFooter} onChange={onChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <label className="inline-flex items-center gap-3 text-sm font-semibold text-gray-800">
            <input
              type="checkbox"
              name="signupEnabled"
              checked={Boolean(form.signupEnabled)}
              onChange={onChange}
              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            Allow new company signups
          </label>
        </div>

        <div className="mt-6">
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save System Settings"}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SuperAdminSystemSettings;
